import { Link } from 'react-router-dom';
import LandingNav from '../components/LandingNav';
import Footer from '../components/Footer';
import PageSEO from '../components/PageSEO';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <PageSEO
        title="About | MomentSent Founder Pete Cote, US Marine Corps Veteran"
        description="MomentSent was founded by Pete Cote, a retired US Marine Corps veteran who built the service to stay connected with the people he loves no matter how busy life gets. Learn the story."
        canonical="https://momentsent.com/about"
      />
      <LandingNav />

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="pt-28 pb-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-12 md:gap-16">

            {/* Text — left on desktop, below photo on mobile */}
            <div className="flex-1 order-2 md:order-1 text-center md:text-left">
              <p className="font-body text-sm text-sage font-semibold tracking-widest uppercase mb-4">
                Meet the Founder
              </p>
              <h1 className="font-heading text-4xl md:text-5xl text-navy leading-tight mb-5">
                Pete Cote
              </h1>
              <p className="font-body text-base text-gray-500 leading-relaxed mb-2 font-semibold">
                Founder &amp; CEO of MomentSent
              </p>
              <p className="font-body text-sm text-gray-400 mb-8">
                United States Marine Corps (Retired)
              </p>
              <p className="font-body text-lg text-navy/80 italic leading-relaxed">
                "I built MomentSent because the people I love deserved better than my busy schedule."
              </p>
            </div>

            {/* Photo — right on desktop, top on mobile */}
            <div className="flex-shrink-0 order-1 md:order-2">
              <img
                src="/pete.png"
                alt="Pete Cote, Founder & CEO of MomentSent"
                className="w-56 h-56 md:w-72 md:h-72 object-cover rounded-2xl shadow-warm-lg
                           mx-auto border-4 border-white"
              />
            </div>

          </div>
        </div>
      </section>

      {/* ── Pull quote ────────────────────────────────────────────────────── */}
      <section className="py-14 px-6 bg-navy">
        <div className="max-w-3xl mx-auto text-center">
          <svg className="w-10 h-10 text-sage opacity-60 mx-auto mb-6" fill="currentColor" viewBox="0 0 32 32" aria-hidden="true">
            <path d="M10 8C6.686 8 4 10.686 4 14v10h10V14H7.5C7.5 11.514 8.962 9.5 10 9.5V8zm14 0c-3.314 0-6 2.686-6 6v10h10V14h-6.5c0-2.486 1.462-4.5 2.5-4.5V8z"/>
          </svg>
          <p className="font-heading text-2xl md:text-3xl text-cream italic leading-relaxed">
            I'd call home after a deployment and find out a birthday had passed a week ago.
            That guilt doesn't go away easily. I needed a system, so I built one.
          </p>
          <p className="mt-6 font-body text-sage text-sm font-semibold tracking-widest uppercase">
            Pete Cote, Founder &amp; CEO
          </p>
        </div>
      </section>

      {/* ── Story ─────────────────────────────────────────────────────────── */}
      <section className="py-20 px-6">
        <div className="max-w-2xl mx-auto space-y-8">

          <div>
            <h2 className="font-heading text-2xl text-navy mb-4">Life moves fast. Moments don't wait.</h2>
            <p className="font-body text-base text-gray-600 leading-relaxed">
              For most of my career, life was moving at a pace that left little room for the
              things that mattered most. Between deployments, field ops, and the relentless pace of
              active duty in the United States Marine Corps, the calendar became an afterthought.
              Birthdays slipped by. Anniversaries passed unacknowledged. Holidays arrived and
              departed while I was somewhere else entirely.
            </p>
          </div>

          <div>
            <p className="font-body text-base text-gray-600 leading-relaxed">
              It wasn't that I didn't care. I cared deeply. It was that caring and
              <em> actually doing something about it</em> require two very different things: intention,
              and time. I had the first in abundance. The second was always in short supply.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-2xl text-navy mb-4">Built for people who run 100mph.</h2>
            <p className="font-body text-base text-gray-600 leading-relaxed">
              After retiring from the Marine Corps, I set out to solve this problem. Not just
              for myself, but for everyone who has ever felt that sting of realizing they missed
              something important. Parents. Entrepreneurs. Shift workers. Anyone whose life doesn't
              slow down just because someone they love has a birthday.
            </p>
          </div>

          <div>
            <p className="font-body text-base text-gray-600 leading-relaxed">
              MomentSent was born from a simple belief: the people in your life shouldn't have to
              wonder if you remembered them. You shouldn't have to fight through a crowded card
              aisle at the last minute, scramble to find a stamp, and cross your fingers hoping
              it arrives on time. There's a better way, and it starts with just a few minutes of
              planning, once.
            </p>
          </div>

        </div>
      </section>

      {/* ── Why it matters callout ────────────────────────────────────────── */}
      <section className="py-6 px-6 pb-20">
        <div className="max-w-2xl mx-auto">
          <div className="bg-cream border-l-4 border-navy rounded-card p-8 shadow-warm">
            <h3 className="font-heading text-xl text-navy mb-4">Why it matters</h3>
            <p className="font-body text-base text-gray-600 leading-relaxed mb-4">
              Receiving a physical card with your name on it, dropped right through your
              mailbox, feels different than a text. It says: <em>you were worth the effort</em>.
              That feeling is irreplaceable. And it's available to everyone, not just the people who
              happen to have a free afternoon to make it to the store.
            </p>
            <p className="font-body text-base text-gray-600 leading-relaxed">
              My mission with MomentSent is to make that feeling accessible for every kind of
              person living every kind of life, so no one ever has to feel forgotten, and no one
              ever has to feel guilty for forgetting.
            </p>
          </div>
        </div>
      </section>

      {/* ── Future vision ─────────────────────────────────────────────────── */}
      <section className="py-10 px-6 pb-20">
        <div className="max-w-2xl mx-auto space-y-6">
          <h2 className="font-heading text-2xl text-navy">Just getting started.</h2>
          <p className="font-body text-base text-gray-600 leading-relaxed">
            I have big plans for where MomentSent is headed. New ways to personalize cards,
            smarter reminders, tools that make it even easier to stay connected to the people
            who matter. I genuinely hope to turn MomentSent into a name that everyone
            recognizes as the company that made sure the people you love always knew you were
            thinking of them.
          </p>
          <p className="font-body text-base text-gray-600 leading-relaxed">
            Whether you're a fellow Marine, a parent, a small business owner, or just someone
            who cares, MomentSent was built for you.
          </p>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────────── */}
      <section className="py-20 px-6 bg-sage/10">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="font-heading text-3xl text-navy mb-4">
            Ready to never miss a moment?
          </h2>
          <p className="font-body text-base text-gray-500 leading-relaxed mb-8">
            Join MomentSent and make sure the people you love always feel remembered, no
            matter how fast life moves.
          </p>
          <Link
            to="/signup"
            className="btn-primary inline-block px-8 py-3 text-base"
          >
            Start your free trial →
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
